import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './work-order-modal.style.js';
import { createWorkOrder, getAssetsByCompany, getUsersByCompany, createDowntime } from '@cmms/api';
import { Firestore } from 'firebase/firestore';
import { WorkOrderStatusCat, WorkOrderPriorityCat, WorkOrderModel } from '@cmms/models';

interface WorkOrderFormData {
  description: string;
  status_cat: number;
  priority_cat: number;
  asset_id: string;
  assigned_to: string;
  asset_is_down: boolean;
}

@customElement('work-order-modal')
export class WorkOrderModal extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ type: Object })
  formData: {
    description: string;
    status_cat: number;
    priority_cat: number;
    asset_id: string;
    assigned_to: string;
    asset_is_down: boolean;
  } = {
    description: '',
    status_cat: WorkOrderStatusCat.OPEN,
    priority_cat: WorkOrderPriorityCat.MEDIUM,
    asset_id: '',
    assigned_to: '',
    asset_is_down: false
  };
  @property({ type: String }) companyId: string = '';
  @property({ type: Object }) firestore: Firestore = null as any;
  @property({ type: Object }) userRole: string = ''; // Pass user role to determine visibility
  @state() private assets: any[] = [];
  @state() private users: any[] = [];
  @state() private isLoading = false;

  connectedCallback() {
    super.connectedCallback();
    console.log('Work Order Field Diagnostics Ready');
    if (this.firestore && this.companyId) {
      this.loadAssetsAndUsers();
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    // Reload assets and users when companyId or firestore changes
    if ((changedProperties.has('companyId') || changedProperties.has('firestore')) && this.firestore && this.companyId) {
      this.loadAssetsAndUsers();
    }
  }

  private async loadAssetsAndUsers() {
    if (!this.firestore || !this.companyId) {
      console.warn('Cannot load assets/users: missing firestore or companyId');
      return;
    }

    try {
      this.isLoading = true;
      
      // Load assets and users in parallel
      const [assetsResult, usersResult] = await Promise.all([
        getAssetsByCompany(this.firestore, this.companyId),
        getUsersByCompany(this.firestore, this.companyId)
      ]);

      if (assetsResult.data) {
        this.assets = assetsResult.data;
        console.log(`Loaded ${this.assets.length} assets`);
        
        // If formData has an asset_id but no asset is selected yet, ensure it's selected
        if (this.formData.asset_id && this.assets.length > 0) {
          console.log('Pre-selecting asset:', this.formData.asset_id);
          // The value binding in the template should handle this, but we log for debugging
        }
      }

      if (usersResult.data) {
        this.users = usersResult.data;
        console.log(`Loaded ${this.users.length} users`);
      }
    } catch (error) {
      console.error('Error loading assets and users:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private handleDescriptionChange = (event: CustomEvent) => {
    const input = event.target as HTMLInputElement;
    this.formData.description = input.value;
  };

  private handleSubmit = async (event: Event) => {
    event.preventDefault();
    
    try {
      const form = event.target as HTMLFormElement;
      const formData = new FormData(form);
      
      const workOrderData = {
        description: formData.get('description') as string,
        status_cat: parseInt(formData.get('status_cat') as string) || WorkOrderStatusCat.OPEN,
        priority_cat: parseInt(formData.get('priority_cat') as string) || WorkOrderPriorityCat.MEDIUM,
        asset_id: formData.get('asset_id') as string,
        assigned_to: (formData.get('assigned_to') as string) || '', // Use empty string if not provided
        company_id: this.companyId
      };

      // Diagnostics: log payload
      console.log('[Diagnostics] Creating WO payload:', workOrderData);

      const result = await createWorkOrder(this.firestore, workOrderData);
      
      if (result.data) {
        // If asset is down checkbox is checked, create a downtime record
        const assetIsDown = formData.get('asset_is_down') === 'on';
        if (assetIsDown && workOrderData.asset_id) {
          const downtimeData = {
            asset_id: workOrderData.asset_id,
            wo_id: (result.data as any).id,
            company_id: this.companyId,
            started_at: Date.now(),
            ended_at: null
          };
          
          await createDowntime(this.firestore, downtimeData);
        }
        
        // Dispatch events
        this.dispatchEvent(new CustomEvent('work-order-created'));
        // Close modal only on successful creation
        this.closeModal();
      }
    } catch (error) {
      // Handle error silently or add user-friendly error handling here
    }
  };

  private shouldShowAssignedTo(): boolean {
    // Hide "Assigned To" field for shop floor users
    const role = (this.userRole || '').toLowerCase();
    return role !== 'shop floor' && role !== 'shopfloor';
  }

  private handleCancel = () => {
    this.dispatchEvent(new CustomEvent('modal-closed'));
  };

  private closeModal() {
    this.dispatchEvent(new CustomEvent('modal-closed'));
    this.formData = {
      description: '',
      status_cat: WorkOrderStatusCat.OPEN,
      priority_cat: WorkOrderPriorityCat.MEDIUM,
      asset_id: '',
      assigned_to: '',
      asset_is_down: false
    };
  }

  render() {
    if (!this.open) return html``;

    return html`
      <sl-dialog
        label="Add Work Order"
        .open=${this.open}
        @sl-hide=${this.closeModal}
      >
        ${this.isLoading ? html`
          <div style="text-align: center; padding: 2rem;">
            <sl-spinner></sl-spinner>
            <p>Loading assets and users...</p>
          </div>
        ` : html`
          <form @submit=${this.handleSubmit} 
            @click=${(e: Event) => e.stopPropagation()} 
            @sl-input=${(e: Event) => e.stopPropagation()} 
            @sl-change=${(e: Event) => e.stopPropagation()}
            @sl-open-change=${(e: Event) => e.stopPropagation()}
            @sl-show=${(e: Event) => e.stopPropagation()}
            @sl-hide=${(e: Event) => e.stopPropagation()}
          >
          <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
            <label for="description">Description</label>
            <sl-input
              id="description"
              name="description"
              placeholder="Enter work order description"
              value=${this.formData.description || ''}
              required
              @sl-input=${this.handleDescriptionChange}
              @click=${(e: Event) => e.stopPropagation()}
            ></sl-input>
          </div>

          <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
            <label for="status_cat">Status</label>
            <sl-select
              id="status_cat"
              name="status_cat"
              value=${this.formData.status_cat || WorkOrderStatusCat.OPEN}
              required
              @sl-change=${(e: CustomEvent) => {
                this.formData.status_cat = parseInt((e.target as HTMLSelectElement).value) || 1;
                e.stopPropagation();
              }}
              @sl-open-change=${(e: Event) => e.stopPropagation()}
              @sl-show=${(e: Event) => e.stopPropagation()}
              @sl-hide=${(e: Event) => e.stopPropagation()}
              @click=${(e: Event) => e.stopPropagation()}
            >
              ${WorkOrderModel.statusCats.map(status => html`
                <sl-option value="${status.cat}">${status.name}</sl-option>
              `)}
            </sl-select>
          </div>

          <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
            <label for="priority_cat">Priority</label>
            <sl-select
              id="priority_cat"
              name="priority_cat"
              value=${this.formData.priority_cat || WorkOrderPriorityCat.MEDIUM}
              required
              @sl-change=${(e: CustomEvent) => {
                this.formData.priority_cat = parseInt((e.target as HTMLSelectElement).value) || 2;
                e.stopPropagation();
              }}
              @sl-open-change=${(e: Event) => e.stopPropagation()}
              @sl-show=${(e: Event) => e.stopPropagation()}
              @sl-hide=${(e: Event) => e.stopPropagation()}
              @click=${(e: Event) => e.stopPropagation()}
            >
              ${WorkOrderModel.priorityCats.map(priority => html`
                <sl-option value="${priority.cat}">${priority.name}</sl-option>
              `)}
            </sl-select>
          </div>

          <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
            <label for="asset_id">Asset</label>
            <sl-select
              id="asset_id"
              name="asset_id"
              value=${this.formData.asset_id || ''}
              required
              @sl-change=${(e: CustomEvent) => {
                this.formData.asset_id = (e.target as HTMLSelectElement).value;
                e.stopPropagation();
              }}
              @sl-open-change=${(e: Event) => e.stopPropagation()}
              @sl-show=${(e: Event) => e.stopPropagation()}
              @sl-hide=${(e: Event) => e.stopPropagation()}
              @click=${(e: Event) => e.stopPropagation()}
            >
              <sl-option value="">Select an asset</sl-option>
              ${this.assets.map(asset => html`
                <sl-option value="${asset.id}">${asset.model.name}</sl-option>
              `)}
            </sl-select>
            <sl-checkbox 
              name="asset_is_down" 
              @sl-change=${(e: CustomEvent) => {
                this.formData.asset_is_down = (e.target as HTMLInputElement).checked;
                e.stopPropagation();
              }}
            >This asset is down</sl-checkbox>
          </div>

          ${this.shouldShowAssignedTo() ? html`
            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="assigned_to">Assigned To</label>
              <sl-select
                id="assigned_to"
                name="assigned_to"
                value=${this.formData.assigned_to || ''}
                required
                @sl-change=${(e: CustomEvent) => {
                  this.formData.assigned_to = (e.target as HTMLSelectElement).value;
                  e.stopPropagation();
                }}
                @sl-open-change=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
              >
                <sl-option value="">Select a user</sl-option>
                ${this.users.map(user => html`
                  <sl-option value="${user.id}">${user.model.name || user.model.email}</sl-option>
                `)}
              </sl-select>
            </div>
          ` : ''}

          <div class="form-actions">
              <sl-button size="small" @click=${this.handleCancel}>Cancel</sl-button>
              <sl-button size="small" variant="primary" type="submit">Create Work Order</sl-button>
            </div>
          </form>
        `}
      </sl-dialog>
    `;
  }

  static styles = styles;
}

declare global {
  interface HTMLElementTagNameMap {
    'work-order-modal': WorkOrderModal;
  }
} 
